import React, { useState, useRef, useEffect } from 'react';

import Widget from './subcomponents/Widget';
import Table, { Props as TableProps } from './subcomponents/Table';

import { Container, Overlay, TableWrapper, BodyFixed } from './CourseCompare.styled';
import { CourseCompareCardProps } from './subcomponents/Card';
import { Course } from 'Zustand/courseCompare';

export type Props = Omit<TableProps, 'close'>;

const CourseCompare: React.FC<Props> = props => {
    const { courses, removeCourse, clearCourses } = props;
    const [isOpen, setIsOpen] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    const courseCardArray = courses.map(
        (course): CourseCompareCardProps => ({
            id: course.id,
            degreeType: course.degreeType && course.degreeType,
            course: course.name,
            image: {
                src: course.image.src
            },
            closeButtonFnc: () => removeCourse(course)
        })
    );

    const uxRemoveCourse = (course: Course) => {
        removeCourse(course);
        if (courses.length - 1 < 2) {
            // Close the modal if courses.length drops below 2 as the user now needs to add another course
            setIsOpen(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Tab' && modalRef.current) {
            const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
                'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
            );
            if (focusableElements.length === 0) {
                e.preventDefault();
                return;
            }
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (!e.shiftKey && document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
            if (e.shiftKey && document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        }
        if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    // Set focus on modal when it opens
    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.focus();
        }
    }, [isOpen]);

    const isModalOpen = courses.length > 1 && isOpen;

    return (
        <>
            {isModalOpen && (
                <>
                    <BodyFixed />
                    <Overlay
                        onClick={() => setIsOpen(false)}
                        role="button"
                        aria-label="Click the overlay background to close the modal"
                    />
                </>
            )}
            {!isOpen && (
                <Widget
                    onCompare={() => setIsOpen(!isOpen)}
                    clearCourses={clearCourses}
                    courses={courseCardArray}
                    aria-controls="course-compare-information"
                    className="m-course-compare m-course-compare-open"
                />
            )}
            <Container $animate={isModalOpen} aria-hidden={!isModalOpen}>
                <TableWrapper
                    id="course-compare-information"
                    $isOpen={isOpen}
                    aria-hidden={!isOpen}
                    role="dialog"
                    aria-modal={true}
                    aria-label="Course comparison information"
                    onKeyDown={handleKeyDown}
                    ref={modalRef}
                >
                    <Table
                        courses={courses}
                        clearCourses={clearCourses}
                        removeCourse={uxRemoveCourse}
                        close={() => setIsOpen(false)}
                    />
                </TableWrapper>
            </Container>
        </>
    );
};

export default CourseCompare;
